import {getCookie, deleteCookie, clearCookies} from './utils/helpers/cookie';
import {KEY_AUTHENTICATED, KEY_AUTHENTICATION_TOKEN} from './utils/constants';

/**
 * Web implementation of the `@tenant/core` platform contract.
 *
 * Behaviour here is deliberately identical to what the app did before the core
 * extraction: auth lives in cookies, fetch sends the cookie jar, and an
 * unrecoverable session does a hard navigation to the root.
 *
 * @see packages/core/platform/contract.js
 */
const webPlatform = {
  name: 'web',

  env: (name, fallback) => {
    if (process && process.env && process.env[name] !== undefined) {
      return process.env[name];
    }
    return fallback;
  },

  /** Non-sensitive session flags. Sync by contract. */
  storage: {
    getItem: key => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
    removeItem: key => localStorage.removeItem(key),
  },

  /**
   * Credentials. Async by contract to match native's secure-enclave reads;
   * on web the cookie reads are synchronous under the promise.
   */
  auth: {
    getToken: async () => getCookie(KEY_AUTHENTICATION_TOKEN) || null,
    isAuthenticated: async () => getCookie(KEY_AUTHENTICATED) === 'true',
    clear: async () => {
      deleteCookie(KEY_AUTHENTICATED);
      deleteCookie(KEY_AUTHENTICATION_TOKEN);
    },
  },

  navigation: {
    openUrl: (url, {newTab = false} = {}) => {
      if (newTab) {
        window.open(url, '_blank');
        return;
      }
      // eslint-disable-next-line fp/no-mutation
      window.location.href = url;
    },
    toRoot: () => {
      // eslint-disable-next-line fp/no-mutation
      window.location.href = '/';
    },
    getQueryParam: name =>
      new URLSearchParams(window.location.search).get(name),
    scrollToTop: () => {
      window.scrollTo({top: 0, behavior: 'smooth'});
    },
  },

  /** The API still relies on the cookie jar alongside the bearer token. */
  fetchCredentials: 'include',

  /** Not part of the contract — exposed for the logout path. */
  clearAllCookies: clearCookies,
};

export default webPlatform;
